import { IconButton, Stack, Typography, useTheme } from '@mui/material';
import { Point } from 'highcharts';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';

// Components
import { StyledWhiteBox } from 'src/features/PortDetail/config';
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import Table from 'src/components/Table';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import AreaChart from 'src/components/HighChart/AreaChart';

// Utilities
import { IDelayStats, IPortcallTimeliness } from 'src/utils/typings/response';
import { useQueryDataPortcallTimelinessChart } from 'src/services/queries';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import IconFeather from 'src/components/common/Icon';
import { Plus } from 'react-feather';
import { Close } from '@mui/icons-material';

interface IProps {
  listAllDelayStats: Array<IDelayStats>;
  locode: string;
}

const PortcallDelayTable = ({ listAllDelayStats, locode }: IProps) => {
  const theme = useTheme();
  const [showTableTimeliness, setShowTableTimeliness] = useState(false);
  const { data, isFetching } = useQueryDataPortcallTimelinessChart({ locode });
  const dataPortcallDelayChart = getArrayDefault(data?.data);

  const configColumn: ConfigCell<IDelayStats>[] = [
    {
      id: 'cause_of_delays',
      disablePadding: true,
      disableSort: true,
      label: 'Cause of delays (%)',
      cell: (row: IDelayStats) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row?.cause_of_delays)}
          </Typography>
        );
      },
    },
    {
      id: 'last_week',
      disablePadding: true,
      disableSort: true,
      label: 'Last Week',
      cell: (row: IDelayStats) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row?.last_week)}
          </Typography>
        );
      },
    },
    {
      id: 'last_quarter',
      disablePadding: true,
      disableSort: true,
      label: 'Last Quarter',
      cell: (row: IDelayStats) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row?.last_quarter)}
          </Typography>
        );
      },
    },
  ];
  const configColumnPortcallTimeliness: ConfigCell<IPortcallTimeliness>[] = [
    {
      id: 'date',
      disablePadding: true,
      disableSort: true,
      label: 'Date',
      cell: (row: IPortcallTimeliness) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row?.date)}
          </Typography>
        );
      },
    },
    {
      id: 'on_time',
      disablePadding: true,
      disableSort: true,
      label: 'On time',
      cell: (row: IPortcallTimeliness) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(row?.on_time)}
          </Typography>
        );
      },
    },
    {
      id: 'others',
      disablePadding: true,
      disableSort: true,
      label: 'Others',
      cell: (row: IPortcallTimeliness) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(row?.others)}
          </Typography>
        );
      },
    },
    {
      id: 'weather',
      disablePadding: true,
      disableSort: true,
      label: 'Weather',
      cell: (row: IPortcallTimeliness) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(row?.weather)}
          </Typography>
        );
      },
    },
    {
      id: 'operational_reasons',
      disablePadding: true,
      disableSort: true,
      label: 'POR',
      cell: (row: IPortcallTimeliness) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(row?.operational_reasons)}
          </Typography>
        );
      },
    },
    {
      id: 'previous_delays',
      disablePadding: true,
      disableSort: true,
      label: 'Previous delays',
      cell: (row: IPortcallTimeliness) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(row?.previous_delays)}
          </Typography>
        );
      },
    },
  ];

  const lineChartYData = useMemo(() => {
    const previousDelaysData: Array<number> = [];
    const operationalReasonsData: Array<number> = [];
    const weatherData: Array<number> = [];
    const onTimeData: Array<number> = [];
    const othersData: Array<number> = [];
    dataPortcallDelayChart.map((item: IPortcallTimeliness) => {
      previousDelaysData.push(item.previous_delays || 0);
      operationalReasonsData.push(item.operational_reasons || 0);
      weatherData.push(item.weather || 0);
      onTimeData.push(item.on_time || 0);
      othersData.push(item.others || 0);
    });
    return { previousDelaysData, operationalReasonsData, weatherData, onTimeData, othersData };
  }, [dataPortcallDelayChart]);

  const lineChartXData = useMemo(() => {
    const res = dataPortcallDelayChart.map((item: IPortcallTimeliness) => {
      return dayjs(item?.date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_ONLY_DATE);
    });
    return res;
  }, [dataPortcallDelayChart]);

  return (
    <StyledWhiteBox display="flex" flexDirection="column" gap={2}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h5" align="left">
          Portcall timeliness/delays (%)
        </Typography>
        <IconButton onClick={() => setShowTableTimeliness(!showTableTimeliness)}>
          {showTableTimeliness ? <Close /> : <IconFeather icon={<Plus />} />}
        </IconButton>
      </Stack>
      <Stack mb={3} spacing={3} direction={'row'} alignItems="flex-start" flex={1}>
        {/* <Stack flex={1}>
          <Table
            data={listAllDelayStats}
            configColumn={configColumn}
            dataKey="cause_of_delays"
            isPagination={false}
            maxRow={10}
            totalRecords={getArrayDefault(listAllDelayStats).length}
            sxTableContainer={{
              border: `1px solid ${theme.palette.divider}`,
            }}
          />
        </Stack> */}
        <Stack flex={1}>
          {showTableTimeliness ? (
            <Table
              data={dataPortcallDelayChart}
              configColumn={configColumnPortcallTimeliness}
              dataKey="date"
              isPagination={false}
              maxRow={5}
              totalRecords={getArrayDefault(dataPortcallDelayChart).length}
            />
          ) : (
            <AreaChart
              loading={isFetching}
              yAxis={{
                labels: {
                  format: '{value}',
                },
              }}
              xAxis={{
                allowDecimals: false,
                labels: {
                  formatter: function () {
                    const month = String(this.value).split('/')[1]; // Extract month from categories
                    const year = String(this.value).split('/')[2]; // Extract year from categories
                    return `${month}/${year}`;
                  },
                },
                categories: lineChartXData,
              }}
              tooltipFormatter={function () {
                const point: Point = this.point;
                return `<span>${point.series.name}</span> : <b>${renderNumberOrDefault(point.y || 0)}</b><br/>`;
              }}
              plotOptions={{
                series: {
                  // pointStart: 2022,
                  marker: {
                    enabled: false,
                    fillColor: theme.palette.primary.main,
                    lineWidth: 0.5,
                    radius: 5,
                    symbol: 'circle',
                  },
                },
                area: {
                  stacking: 'percent',
                  marker: {
                    enabled: false,
                  },
                },
              }}
              legend={{
                enabled: true,
                align: 'center',
                verticalAlign: 'bottom',
                backgroundColor: 'white',
                borderColor: 'transparent',
                borderWidth: 1,
                shadow: false,
              }}
              series={[
                {
                  name: 'Previous delays',
                  type: 'area',
                  lineColor: '#4338CA',
                  fillColor: '#4338CA',
                  data: lineChartYData.previousDelaysData,
                },
                {
                  name: 'Port operational reasons',
                  type: 'area',
                  lineColor: '#6366F1',
                  fillColor: '#6366F1',
                  data: lineChartYData.operationalReasonsData,
                },
                {
                  name: 'Weather',
                  type: 'area',
                  lineColor: '#A5B4FC',
                  fillColor: '#A5B4FC',
                  data: lineChartYData.weatherData,
                },
                {
                  name: 'On time',
                  type: 'area',
                  lineColor: '#C7D2FE',
                  fillColor: '#C7D2FE',
                  data: lineChartYData.onTimeData,
                },
                {
                  name: 'Others',
                  type: 'area',
                  lineColor: '#EEF2FF',
                  fillColor: '#EEF2FF',
                  data: lineChartYData.othersData,
                },
              ]}
            />
          )}
        </Stack>
      </Stack>
    </StyledWhiteBox>
  );
};

export default PortcallDelayTable;
